'use client';

import { initials as toInitials } from '@utazzo/shared';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '../../primitives/avatar';
import { cn } from '../../utils/cn';

const avatarVariants = cva('', {
  variants: {
    size: {
      xs: 'h-6 w-6 text-[10px]',
      sm: 'h-8 w-8 text-xs',
      md: 'h-10 w-10 text-sm',
      lg: 'h-12 w-12 text-base',
      xl: 'h-16 w-16 text-lg',
    },
  },
  defaultVariants: { size: 'md' },
});

/**
 * Soft tinted background + readable text pairs for initials avatars. Light bg /
 * dark text, matching the design language. The palette is deliberately smaller
 * than the alphabet, so letters wrap around and share colours (A…Z repeat).
 */
const AVATAR_TONES = [
  'bg-red-100 text-red-700',
  'bg-orange-100 text-orange-700',
  'bg-amber-100 text-amber-700',
  'bg-lime-100 text-lime-700',
  'bg-green-100 text-green-700',
  'bg-emerald-100 text-emerald-700',
  'bg-teal-100 text-teal-700',
  'bg-cyan-100 text-cyan-700',
  'bg-sky-100 text-sky-700',
  'bg-blue-100 text-blue-700',
  'bg-indigo-100 text-indigo-700',
  'bg-violet-100 text-violet-700',
  'bg-purple-100 text-purple-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-pink-100 text-pink-700',
  'bg-rose-100 text-rose-700',
] as const;

/**
 * Pick a stable tone from a seed string. Deterministic so the same person
 * always gets the same colour. Hashes the whole seed (not just the first
 * letter) for better spread while staying consistent.
 */
export function avatarTone(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length]!;
}

export interface UzAvatarProps
  extends
    Omit<ComponentPropsWithoutRef<typeof Avatar>, 'children'>,
    VariantProps<typeof avatarVariants> {
  src?: string | null;
  /** Full name. Generates fallback initials when no image. */
  name: string;
  /** Override the auto-generated initials. */
  initials?: string;
  /**
   * Colour the initials fallback with a deterministic tinted background derived
   * from the name. Defaults to true. Set false for a neutral muted fallback.
   */
  colorful?: boolean;
}

export const UzAvatar = forwardRef<ElementRef<typeof Avatar>, UzAvatarProps>(
  ({ src, name, initials, size, colorful = true, className, ...rest }, ref) => {
    const ini = initials ?? toInitials(name);
    return (
      <Avatar ref={ref} className={cn(avatarVariants({ size }), className)} {...rest}>
        {src && <AvatarImage src={src} alt={name} />}
        <AvatarFallback className={colorful ? avatarTone(name) : undefined}>{ini}</AvatarFallback>
      </Avatar>
    );
  },
);
UzAvatar.displayName = 'UzAvatar';
